import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {COMMA, ENTER, TAB} from '@angular/cdk/keycodes';

import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {ContactTypeService, CountryCodeService, DataService, GroupsService, GroupTypeService, IndividualsService, SnackbarService} from 'src/app/services';
import {ContactType, CountryCode, GroupType} from 'src/app/models';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {GroupTypePopupComponent} from './groupTypePopup/group-type-popup.component';
import {ContactTypePopupComponent} from './contactTypePopup/contact-type-popup.component';
import {RateLimitOverridePopupComponent} from './rateLimitOverridePopup/rate-limit-override-popup.component';
import {ConfirmationDialogComponent} from 'src/app/components';
import {RateLimitOverrideService} from 'src/app/services';
import {RateLimitOverride} from 'src/app/models';
import {MatDialog} from '@angular/material/dialog';

const ZERO_TO_99 = RegExp('^(0?[1-9]|[1-9][0-9])$');
const ONE_TO_30 = RegExp('^(0?[1-9]|[1-2][0-9]|30)$');
const POSTIVE_NUMBER = RegExp('^[0-9]+$');
const ONE_OR_MORE = RegExp('^[1-9][0-9]*$');
const DD_HH_MM = RegExp('^[0-9]{2}:[0-9]{2}:[0-9]{2}$');

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {
    isValid = true;
    separatorKeysCodes: number[] = [ENTER, COMMA, TAB];
    groupTypes: GroupType[];
    groupTypesToRemove: GroupType[] = [];
    groupTypesToAdd: GroupType[] = [];
    contactTypes: ContactType[];
    contactTypesToRemove: ContactType[] = [];
    contactTypesToAdd: ContactType[] = [];
    individualsAuto = null;
    superGroupMemberInput = '';
    groupMemberInput = '';
    groupOwnerInput = '';
    indsAndDevices = null;
    groupsAuto = null;
    groupTypeName;
    contactTypeName;

    pageLoad = false;
    requestCount = 0;
    refreshingCache = false;
    lastCacheRefresh: Date = null;

    NEWS = 'NEWS';
    NEWS_COLOR = 'NEWS_COLOR';
    DEFAULT_GROUP_OWNERS = 'DEFAULT_GROUP_OWNERS';
    DEFAULT_GROUP_MEMBERS = 'DEFAULT_GROUP_MEMBERS';
    DEFAULT_SUPER_GROUP_MEMBERS = 'DEFAULT_SUPER_GROUP_MEMBERS';

    DO_LDAP_SYNC = 'DO_AD_SYNC';
    LDAP_SYNC_JOB_FREQUENCY = 'AD_SYNC_JOB_FREQUENCY';
    LDAP_SYNC_JOB_ERROR = 'AD_SYNC_JOB_ERROR';
    LAST_LDAP_SYNC_ATTEMPT_TIMESTAMP = 'LAST_AD_SYNC_ATTEMPT_TIMESTAMP';
    LAST_LDAP_SYNC_SUCCESS_TIMESTAMP = 'LAST_AD_SYNC_SUCCESS_TIMESTAMP';

    DO_ESCAL = 'DO_ESCAL';
    ESCAL_JOB_FREQUENCY = 'ESCAL_JOB_FREQUENCY';
    ESCAL_JOB_ERROR = 'ESCAL_JOB_ERROR';
    LAST_ESCAL_ATTEMPT_TIMESTAMP = 'LAST_ESCAL_ATTEMPT_TIMESTAMP';
    LAST_ESCAL_SUCCESS_TIMESTAMP = 'LAST_ESCAL_SUCCESS_TIMESTAMP';
    ESCALATION_INTERVAL = 'ESCALATION_INTERVAL';
    REMINDER_INTERVAL = 'REMINDER_INTERVAL';

    DO_MESSAGE_PURGE = 'DO_MESSAGE_PURGE';
    MESSAGE_PURGE_JOB_FREQUENCY = 'MESSAGE_PURGE_JOB_FREQUENCY';
    MESSAGE_PURGE_JOB_ERROR = 'MESSAGE_PURGE_JOB_ERROR';
    LAST_MESSAGE_PURGE_ATTEMPT_TIMESTAMP = 'LAST_MESSAGE_PURGE_ATTEMPT_TIMESTAMP';
    LAST_MESSAGE_PURGE_SUCCESS_TIMESTAMP = 'LAST_MESSAGE_PURGE_SUCCESS_TIMESTAMP';
    DO_INDIVIDUAL_PURGE = 'DO_INDIVIDUAL_PURGE';
    INDIVIDUAL_PURGE_JOB_FREQUENCY = 'INDIVIDUAL_PURGE_JOB_FREQUENCY';
    INDIVIDUAL_PURGE_JOB_ERROR = 'INDIVIDUAL_PURGE_JOB_ERROR';
    LAST_INDIVIDUAL_PURGE_ATTEMPT_TIMESTAMP = 'LAST_INDIVIDUAL_PURGE_ATTEMPT_TIMESTAMP';
    LAST_INDIVIDUAL_PURGE_SUCCESS_TIMESTAMP = 'LAST_INDIVIDUAL_PURGE_SUCCESS_TIMESTAMP';
    DO_CONTACT_PURGE = 'DO_CONTACT_PURGE';
    CONTACT_PURGE_JOB_FREQUENCY = 'CONTACT_PURGE_JOB_FREQUENCY';
    CONTACT_PURGE_JOB_ERROR = 'CONTACT_PURGE_JOB_ERROR';
    LAST_CONTACT_PURGE_ATTEMPT_TIMESTAMP = 'LAST_CONTACT_PURGE_ATTEMPT_TIMESTAMP';
    LAST_CONTACT_PURGE_SUCCESS_TIMESTAMP = 'LAST_CONTACT_PURGE_SUCCESS_TIMESTAMP';
    DO_DEVICE_PURGE = 'DO_DEVICE_PURGE';
    DEVICE_PURGE_JOB_FREQUENCY = 'DEVICE_PURGE_JOB_FREQUENCY';
    DEVICE_PURGE_JOB_ERROR = 'DEVICE_PURGE_JOB_ERROR';
    LAST_DEVICE_PURGE_ATTEMPT_TIMESTAMP = 'LAST_DEVICE_PURGE_ATTEMPT_TIMESTAMP';
    LAST_DEVICE_PURGE_SUCCESS_TIMESTAMP = 'LAST_DEVICE_PURGE_SUCCESS_TIMESTAMP';
    DO_GROUP_PURGE = 'DO_GROUP_PURGE';
    GROUP_PURGE_JOB_FREQUENCY = 'GROUP_PURGE_JOB_FREQUENCY';
    GROUP_PURGE_JOB_ERROR = 'GROUP_PURGE_JOB_ERROR';
    LAST_GROUP_PURGE_ATTEMPT_TIMESTAMP = 'LAST_GROUP_PURGE_ATTEMPT_TIMESTAMP';
    LAST_GROUP_PURGE_SUCCESS_TIMESTAMP = 'LAST_GROUP_PURGE_SUCCESS_TIMESTAMP';
    DO_ORPHAN_PURGE = 'DO_ORPHAN_PURGE';
    ORPHAN_PURGE_JOB_FREQUENCY = 'ORPHAN_PURGE_JOB_FREQUENCY';
    ORPHAN_PURGE_JOB_ERROR = 'ORPHAN_PURGE_JOB_ERROR';
    LAST_ORPHAN_PURGE_ATTEMPT_TIMESTAMP = 'LAST_ORPHAN_PURGE_ATTEMPT_TIMESTAMP';
    LAST_ORPHAN_PURGE_SUCCESS_TIMESTAMP = 'LAST_ORPHAN_PURGE_SUCCESS_TIMESTAMP';
    DO_TEMPLATE_PURGE = 'DO_TEMPLATE_PURGE';
    TEMPLATE_PURGE_JOB_FREQUENCY = 'TEMPLATE_PURGE_JOB_FREQUENCY';
    TEMPLATE_PURGE_JOB_ERROR = 'TEMPLATE_PURGE_JOB_ERROR';
    LAST_TEMPLATE_PURGE_ATTEMPT_TIMESTAMP = 'LAST_TEMPLATE_PURGE_ATTEMPT_TIMESTAMP';
    LAST_TEMPLATE_PURGE_SUCCESS_TIMESTAMP = 'LAST_TEMPLATE_PURGE_SUCCESS_TIMESTAMP';
    DO_TOKEN_PURGE = 'DO_TOKEN_PURGE';
    TOKEN_PURGE_JOB_FREQUENCY = 'TOKEN_PURGE_JOB_FREQUENCY';
    TOKEN_PURGE_JOB_ERROR = 'TOKEN_PURGE_JOB_ERROR';
    LAST_TOKEN_PURGE_ATTEMPT_TIMESTAMP = 'LAST_TOKEN_PURGE_ATTEMPT_TIMESTAMP';
    LAST_TOKEN_PURGE_SUCCESS_TIMESTAMP = 'LAST_TOKEN_PURGE_SUCCESS_TIMESTAMP';
    MESSAGE_RETENTION = 'MESSAGE_RETENTION';

    DO_ON_CALL = 'DO_ON_CALL';
    ON_CALL_JOB_FREQUENCY = 'ON_CALL_JOB_FREQUENCY';
    ON_CALL_JOB_ERROR = 'ON_CALL_JOB_ERROR';
    LAST_ON_CALL_ATTEMPT_TIMESTAMP = 'LAST_ON_CALL_ATTEMPT_TIMESTAMP';
    LAST_ON_CALL_SUCCESS_TIMESTAMP = 'LAST_ON_CALL_SUCCESS_TIMESTAMP';
    SCHEDULE_PURGE_AGE = 'SCHEDULE_PURGE_LENGTH';

    NOTIFICATION_OVERRIDE = 'NOTIFICATION_OVERRIDE';
    DEFAULT_NOTIFICATION = 'DEFAULT_NOTIFICATION';
    RECIPIENT_WARNING = 'RECIPIENT_WARNING';
    TOKEN_EXPIRATION = 'TOKEN_EXPIRATION';
    MESSAGE_STATUS_V2_REFRESH_INTERVAL = 'MESSAGE_STATUS_V2_REFRESH_INTERVAL';

    FF_MESSAGE_TYPE = 'FF_MessageType';
    FF_MESSAGE_V2 = 'FF_MessageV2';
    FF_ALERTMANAGER_V2 = 'FF_AlertManagerV2';
    FF_PAGERDUTY_V2 = 'FF_PagerDutyV2';
    FF_ESCALATION_V2 = 'FF_EscalationV2';
    FF_ACK_DEEP_LINK = 'FF_AckDeepLink';

    AM_ALERTS_CAP_MODE = 'AM_ALERTS_CAP_MODE';
    AM_ALERTS_CAP = 'AM_ALERTS_CAP';
    AM_ALERTS_CAP_MODES = ['OFF', 'LOG', 'ENFORCE'];

    rateLimitOverrides: RateLimitOverride[] = [];
    rateLimitOverrideColumns = ['caller', 'route_group', 'replenish_rate', 'burst_capacity', 'alert_cap', 'ticket', 'expires_at', 'status', 'last_modified_date', 'last_modified_by', 'actions'];
    routeGroupLabels = {message: 'Message', alertManager: 'Alert Manager', pagerduty: 'Pager Duty'};

    settings = {
        NEWS: '',
        NEWS_COLOR: '',
        DEFAULT_GROUP_OWNERS: '',
        DEFAULT_GROUP_MEMBERS: '',
        DEFAULT_SUPER_GROUP_MEMBERS: '',

        DO_LDAP_SYNC: '',
        LDAP_SYNC_JOB_FREQUENCY: '',
        LDAP_SYNC_JOB_ERROR: '',
        LAST_LDAP_SYNC_ATTEMPT_TIMESTAMP: '',
        LAST_LDAP_SYNC_SUCCESS_TIMESTAMP: '',

        DO_ESCAL: '',
        ESCAL_JOB_FREQUENCY: '',
        ESCAL_JOB_ERROR: '',
        LAST_ESCAL_ATTEMPT_TIMESTAMP: '',
        LAST_ESCAL_SUCCESS_TIMESTAMP: '',
        ESCALATION_INTERVAL: '',
        REMINDER_INTERVAL: '',

        DO_PURGE: '',
        PURGE_JOB_FREQUENCY: '',
        PURGE_JOB_ERROR: '',
        LAST_PURGE_ATTEMPT_TIMESTAMP: '',
        LAST_PURGE_SUCCESS_TIMESTAMP: '',
        MESSAGE_RETENTION: '',

        DO_MESSAGE_PURGE: '',
        MESSAGE_PURGE_JOB_FREQUENCY: '',
        MESSAGE_PURGE_JOB_ERROR: '',
        LAST_MESSAGE_PURGE_ATTEMPT_TIMESTAMP: '',
        LAST_MESSAGE_PURGE_SUCCESS_TIMESTAMP: '',

        DO_INDIVIDUAL_PURGE: '',
        INDIVIDUAL_PURGE_JOB_FREQUENCY: '',
        INDIVIDUAL_PURGE_JOB_ERROR: '',
        LAST_INDIVIDUAL_PURGE_ATTEMPT_TIMESTAMP: '',
        LAST_INDIVIDUAL_PURGE_SUCCESS_TIMESTAMP: '',

        DO_CONTACT_PURGE: '',
        CONTACT_PURGE_JOB_FREQUENCY: '',
        CONTACT_PURGE_JOB_ERROR: '',
        LAST_CONTACT_PURGE_ATTEMPT_TIMESTAMP: '',
        LAST_CONTACT_PURGE_SUCCESS_TIMESTAMP: '',

        DO_DEVICE_PURGE: '',
        DEVICE_PURGE_JOB_FREQUENCY: '',
        DEVICE_PURGE_JOB_ERROR: '',
        LAST_DEVICE_PURGE_ATTEMPT_TIMESTAMP: '',
        LAST_DEVICE_PURGE_SUCCESS_TIMESTAMP: '',

        DO_GROUP_PURGE: '',
        GROUP_PURGE_JOB_FREQUENCY: '',
        GROUP_PURGE_JOB_ERROR: '',
        LAST_GROUP_PURGE_ATTEMPT_TIMESTAMP: '',
        LAST_GROUP_PURGE_SUCCESS_TIMESTAMP: '',

        DO_ORPHAN_PURGE: '',
        ORPHAN_PURGE_JOB_FREQUENCY: '',
        ORPHAN_PURGE_JOB_ERROR: '',
        LAST_ORPHAN_PURGE_ATTEMPT_TIMESTAMP: '',
        LAST_ORPHAN_PURGE_SUCCESS_TIMESTAMP: '',

        DO_TEMPLATE_PURGE: '',
        TEMPLATE_PURGE_JOB_FREQUENCY: '',
        TEMPLATE_PURGE_JOB_ERROR: '',
        LAST_TEMPLATE_PURGE_ATTEMPT_TIMESTAMP: '',
        LAST_TEMPLATE_PURGE_SUCCESS_TIMESTAMP: '',

        DO_TOKEN_PURGE: '',
        TOKEN_PURGE_JOB_FREQUENCY: '',
        TOKEN_PURGE_JOB_ERROR: '',
        LAST_TOKEN_PURGE_ATTEMPT_TIMESTAMP: '',
        LAST_TOKEN_PURGE_SUCCESS_TIMESTAMP: '',

        DO_ON_CALL: '',
        ON_CALL_JOB_FREQUENCY: '',
        ON_CALL_JOB_ERROR: '',
        LAST_ON_CALL_ATTEMPT_TIMESTAMP: '',
        LAST_ON_CALL_SUCCESS_TIMESTAMP: '',
        SCHEDULE_PURGE_AGE: '',

        NOTIFICATION_OVERRIDE: '',
        DEFAULT_NOTIFICATION: '',
        RECIPIENT_WARNING: '',
        TOKEN_EXPIRATION: '',
        MESSAGE_STATUS_V2_REFRESH_INTERVAL: '',

        FF_MESSAGE_TYPE: '',
        FF_MESSAGE_V2: '',
        FF_ALERTMANAGER_V2: '',
        FF_PAGERDUTY_V2: '',
        FF_ESCALATION_V2: '',
        FF_ACK_DEEP_LINK: '',

        AM_ALERTS_CAP_MODE: '',
        AM_ALERTS_CAP: ''
    };
    prevSettings = {};

    groupChange = new Subject<string>();
    individualsChange = new Subject<string>();
    indsAndDevicesChange = new Subject<string>();

    countryCodes: CountryCode[];
    @ViewChild('phoneCodeInput') phoneCodeInput: ElementRef;

    constructor(private dataService: DataService,
                private snackbarService: SnackbarService,
                private individualsService: IndividualsService,
                private groupTypeService: GroupTypeService,
                private contactTypeService: ContactTypeService,
                private groupsService: GroupsService,
                private countryCodeService: CountryCodeService,
                private rateLimitOverrideService: RateLimitOverrideService,
                private dialog: MatDialog
    ) {
    }

    ngOnInit() {
        this.loadRateLimitOverrides();
        const delay = 750;
        this.groupChange.pipe(
            debounceTime(delay),
            distinctUntilChanged()
        ).subscribe((term: string) => this.groupsService.getGroupNames(term).subscribe(g => this.groupsAuto = g));
        this.individualsChange.pipe(
            debounceTime(delay),
            distinctUntilChanged()
        ).subscribe((term: string) => this.individualsService.getIndividuals(term).subscribe(ind => this.individualsAuto = ind));
        this.indsAndDevicesChange.pipe(
            debounceTime(delay),
            distinctUntilChanged()
        ).subscribe((term: string) => this.individualsService.getIndividualsAndDevices(term).subscribe(ind => this.indsAndDevices = ind.content));

        this.dataService.getSettings().subscribe(set => {
            set.forEach(res => {
                switch (res.name) {
                    case this.DO_LDAP_SYNC:
                    case this.DO_ESCAL:
                    case this.DO_MESSAGE_PURGE:
                    case this.DO_INDIVIDUAL_PURGE:
                    case this.DO_CONTACT_PURGE:
                    case this.DO_DEVICE_PURGE:
                    case this.DO_GROUP_PURGE:
                    case this.DO_ORPHAN_PURGE:
                    case this.DO_TEMPLATE_PURGE:
                    case this.DO_TOKEN_PURGE:
                    case this.DO_ON_CALL:
                    case this.NOTIFICATION_OVERRIDE:
                    case this.FF_MESSAGE_TYPE:
                    case this.FF_MESSAGE_V2:
                    case this.FF_ALERTMANAGER_V2:
                    case this.FF_PAGERDUTY_V2:
                    case this.FF_ESCALATION_V2:
                    case this.FF_ACK_DEEP_LINK:
                        this.settings[res.name] = (res.value !== 'false');
                        break;
                    case this.DEFAULT_GROUP_OWNERS:
                    case this.DEFAULT_GROUP_MEMBERS:
                    case this.DEFAULT_SUPER_GROUP_MEMBERS:
                        this.settings[res.name] = res.value.split(',');
                        break;
                    case this.NEWS:
                    case this.NEWS_COLOR:
                    case this.LDAP_SYNC_JOB_FREQUENCY:
                    case this.LDAP_SYNC_JOB_ERROR:
                    case this.LAST_LDAP_SYNC_ATTEMPT_TIMESTAMP:
                    case this.LAST_LDAP_SYNC_SUCCESS_TIMESTAMP:
                    case this.ESCAL_JOB_FREQUENCY:
                    case this.ESCAL_JOB_ERROR:
                    case this.LAST_ESCAL_ATTEMPT_TIMESTAMP:
                    case this.LAST_ESCAL_SUCCESS_TIMESTAMP:
                    case this.ESCALATION_INTERVAL:
                    case this.REMINDER_INTERVAL:
                    case this.MESSAGE_PURGE_JOB_FREQUENCY:
                    case this.MESSAGE_PURGE_JOB_ERROR:
                    case this.LAST_MESSAGE_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_MESSAGE_PURGE_SUCCESS_TIMESTAMP:
                    case this.MESSAGE_RETENTION:
                    case this.INDIVIDUAL_PURGE_JOB_FREQUENCY:
                    case this.INDIVIDUAL_PURGE_JOB_ERROR:
                    case this.LAST_INDIVIDUAL_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_INDIVIDUAL_PURGE_SUCCESS_TIMESTAMP:
                    case this.CONTACT_PURGE_JOB_FREQUENCY:
                    case this.CONTACT_PURGE_JOB_ERROR:
                    case this.LAST_CONTACT_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_CONTACT_PURGE_SUCCESS_TIMESTAMP:
                    case this.DEVICE_PURGE_JOB_FREQUENCY:
                    case this.DEVICE_PURGE_JOB_ERROR:
                    case this.LAST_DEVICE_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_DEVICE_PURGE_SUCCESS_TIMESTAMP:
                    case this.GROUP_PURGE_JOB_FREQUENCY:
                    case this.GROUP_PURGE_JOB_ERROR:
                    case this.LAST_GROUP_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_GROUP_PURGE_SUCCESS_TIMESTAMP:
                    case this.ORPHAN_PURGE_JOB_FREQUENCY:
                    case this.ORPHAN_PURGE_JOB_ERROR:
                    case this.LAST_ORPHAN_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_ORPHAN_PURGE_SUCCESS_TIMESTAMP:
                    case this.TEMPLATE_PURGE_JOB_FREQUENCY:
                    case this.TEMPLATE_PURGE_JOB_ERROR:
                    case this.LAST_TEMPLATE_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_TEMPLATE_PURGE_SUCCESS_TIMESTAMP:
                    case this.TOKEN_PURGE_JOB_FREQUENCY:
                    case this.TOKEN_PURGE_JOB_ERROR:
                    case this.LAST_TOKEN_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_TOKEN_PURGE_SUCCESS_TIMESTAMP:
                    case this.ON_CALL_JOB_FREQUENCY:
                    case this.ON_CALL_JOB_ERROR:
                    case this.LAST_ON_CALL_ATTEMPT_TIMESTAMP:
                    case this.LAST_ON_CALL_SUCCESS_TIMESTAMP:
                    case this.SCHEDULE_PURGE_AGE:
                    case this.RECIPIENT_WARNING:
                    case this.TOKEN_EXPIRATION:
                    case this.DEFAULT_NOTIFICATION:
                    case this.MESSAGE_STATUS_V2_REFRESH_INTERVAL:
                    case this.AM_ALERTS_CAP:
                        this.settings[res.name] = res.value;
                        break;
                    case this.AM_ALERTS_CAP_MODE:
                        this.settings[res.name] = (res.value || '').trim().toUpperCase();
                        break;
                    // No default case because we only want to capture the settings we need for this screen!!!
                }
                this.prevSettings = JSON.parse(JSON.stringify(this.settings)) ;
            });

            this.groupTypeService.getGroupTypes().subscribe(gTypes => {
                this.contactTypeService.getContactTypes().subscribe(cTypes => {
                    this.countryCodeService.getAllCountryCodes().subscribe(e => {
                        this.countryCodes = e;
                        this.groupTypes = gTypes;
                        this.contactTypes = cTypes;
                        this.validate();
                        this.pageLoad = true;
                    });
                });
            });
        });
    }

    validate() {
        this.isValid =
            ZERO_TO_99.test(this.settings[this.ESCALATION_INTERVAL])
            && ZERO_TO_99.test(this.settings[this.REMINDER_INTERVAL])
            && DD_HH_MM.test(this.settings[this.MESSAGE_RETENTION]);
    }

    recipientWarningChange(event) {
        this.isValid = ZERO_TO_99.test(event);
        this.isValid ? this.snackbarService.dismiss() : this.snackbarService.openSnackBar(`${event} is not a valid threshold.`, true);
    }

    EscalateTimeChange(event) {
        this.isValid = ZERO_TO_99.test(event);
        this.isValid ? this.snackbarService.dismiss() : this.snackbarService.openSnackBar(`${event} is not a valid frequency.`, true);
    }

    MessageRetentionChange(event) {
        this.isValid = DD_HH_MM.test(event);
        this.isValid ? this.snackbarService.dismiss() : this.snackbarService.openSnackBar(`${event} is not a valid frequency.`, true);
    }

    deleteScheduleDaysChange(event) {
        this.isValid = POSTIVE_NUMBER.test(event);
        this.isValid ? this.snackbarService.dismiss() : event ? this.snackbarService.openSnackBar(`${event} is not a valid frequency.`, true) : this.snackbarService.openSnackBar(`Please enter a valid frequency for completed schedules to be deleted.`, true);
    }

    tokenExpirationChange(event) {
        this.isValid = ONE_TO_30.test(event);
        this.isValid ? this.snackbarService.dismiss() : this.snackbarService.openSnackBar(`${event} must be between 1 and 30 minutes.`, true);
    }

    alertsCapChange(event) {
        this.isValid = ONE_OR_MORE.test(event);
        this.isValid ? this.snackbarService.dismiss() : this.snackbarService.openSnackBar(`${event} is not a valid alerts cap; enter a whole number of 1 or more.`, true);
    }

    messageStatusV2RefreshChange(event) {
        this.isValid = POSTIVE_NUMBER.test(event);
        this.isValid ? this.snackbarService.dismiss() : this.snackbarService.openSnackBar(`${event} is not a valid refresh interval.`, true);
    }

    removeChip(arrayName, chipValue) {
        this.settings[arrayName] = this.settings[arrayName].filter((arrValue: any) => (chipValue !== arrValue));
    }

    addGroupType(groupTypeToAdd: any) {
        const newGroupType: GroupType = {ordinal: this.lastOrdinal() + 1, group_type: groupTypeToAdd.value, group_type_desc: groupTypeToAdd.value + " Description", use_system_global_intervals: false};

        if (this.groupTypesToRemove.findIndex(gType => gType.group_type.toUpperCase() === newGroupType.group_type.toUpperCase()) !== -1) {
            this.groupTypesToRemove.filter(gType => gType.group_type.toUpperCase() !== newGroupType.group_type.toUpperCase());
            this.groupTypes.push(newGroupType);
            this.groupTypeName = '';
        } else if (this.groupTypesToAdd.findIndex(gType => gType.group_type.toUpperCase() === newGroupType.group_type.toUpperCase()) !== -1
            || this.groupTypes.findIndex(gType => gType.group_type.toUpperCase() === newGroupType.group_type.toUpperCase()) !== -1) {
            this.snackbarService.openSnackBar('Cannot add duplicate group type ' + newGroupType.group_type, true);
        } else {
            this.groupTypes.push(newGroupType);
            this.groupTypesToAdd.push(newGroupType);
            this.groupTypeName = '';
        }
    }

    removeGroupType(groupTypeToRemove) {
        if (this.groupTypesToAdd.findIndex(gType => gType.group_type === groupTypeToRemove.group_type) !== -1) {
            this.groupTypesToAdd = this.groupTypesToAdd.filter(gType => gType.group_type !== groupTypeToRemove.group_type);
            this.groupTypes = this.groupTypes.filter(gType => gType !== groupTypeToRemove);
        } else {
            this.groupTypes = this.groupTypes.filter(gType => gType !== groupTypeToRemove);
            this.groupTypesToRemove.push(groupTypeToRemove);
        }
    }

    addContactType(contactTypeToAdd: any) {
        const newContactType: ContactType = {contact_type_id: this.lastContactId() + 1, contact_type: contactTypeToAdd.value, description: null, admin_only: false};

        if (this.contactTypesToRemove.findIndex(cType => cType.contact_type.toUpperCase() === newContactType.contact_type.toUpperCase()) !== -1) {
            this.contactTypesToRemove.filter(cType => cType.contact_type.toUpperCase() !== newContactType.contact_type.toUpperCase());
            this.contactTypes.push(newContactType);
            this.contactTypeName = '';
        } else if (this.contactTypesToAdd.findIndex(cType => cType.contact_type.toUpperCase() === newContactType.contact_type.toUpperCase()) !== -1
            || this.contactTypes.findIndex(cType => cType.contact_type.toUpperCase() === newContactType.contact_type.toUpperCase()) !== -1) {
            this.snackbarService.openSnackBar('Cannot add duplicate contact type ' + newContactType.contact_type, true);
        } else {
            this.contactTypes.push(newContactType);
            this.contactTypesToAdd.push(newContactType);
            this.contactTypeName = '';
        }
    }

    removeContactType(contactTypeToRemove) {
        if (this.contactTypesToAdd.findIndex(cType => cType.contact_type === contactTypeToRemove.contact_type) !== -1) {
            this.contactTypesToAdd = this.contactTypesToAdd.filter(cType => cType.contact_type !== contactTypeToRemove.contact_type);
            this.contactTypes = this.contactTypes.filter(cType => cType !== contactTypeToRemove);
        } else {
            this.contactTypes = this.contactTypes.filter(cType => cType !== contactTypeToRemove);
            this.contactTypesToRemove.push(contactTypeToRemove);
        }
    }

    canSave() {
        return this.requestCount > 0;
    }

    refreshCache() {
        if (this.refreshingCache) {
            return;
        }
        this.refreshingCache = true;
        this.dataService.refreshSettingsCache().subscribe({
            next: () => {
                this.lastCacheRefresh = new Date();
                this.refreshingCache = false;
                this.snackbarService.openSnackBar('Settings cache refreshed successfully.');
            },
            error: () => {
                this.refreshingCache = false;
                this.snackbarService.openSnackBar('Failed to refresh settings cache.', true);
            }
        });
    }

    incrementRequestCount() {
        this.requestCount++;
    }

    decrementRequestCount() {
        this.requestCount--;
        if (this.requestCount === 0) {
            this.snackbarService.openSnackBar('Saved settings successfully.');
        }
    }

    openGroupTypePopup(groupType: GroupType, index: number) {
        let groupTypePopup = this.dialog.open(GroupTypePopupComponent, {
            width: '65%', maxHeight: '90%', data: groupType
        });
        groupTypePopup.afterClosed().subscribe((res) => {
            if (res) {
                this.groupTypes[index] = res;
                this.groupTypeService.updateGroupType(groupType.group_type_id, res).subscribe();
            }
        });
    }

    openContactTypePopup(contactType: ContactType, index: number) {
        let contactTypePopup = this.dialog.open(ContactTypePopupComponent, {
            width: '65%', maxHeight: '90%', data: contactType
        });
        contactTypePopup.afterClosed().subscribe((res) => {
            if (res) {
                this.contactTypes[index] = res;
                this.contactTypeService.updateContactType(contactType.contact_type_id, res).subscribe();
            }
        });
    }

    loadRateLimitOverrides() {
        this.rateLimitOverrideService.getOverrides().subscribe({
            next: overrides => this.rateLimitOverrides = [...overrides]
                .sort((a, b) => a.caller.localeCompare(b.caller) || a.route_group.localeCompare(b.route_group)),
            error: () => this.snackbarService.openSnackBar('Unable to load rate limit exceptions.', true)
        });
    }

    openRateLimitOverridePopup(override?: RateLimitOverride) {
        this.dialog.open(RateLimitOverridePopupComponent, {
            width: '65%', maxHeight: '90%', data: override ?? null
        }).afterClosed().subscribe((res: RateLimitOverride) => {
            if (!res) {
                return;
            }
            const request = res.id
                ? this.rateLimitOverrideService.updateOverride(res.id, res)
                : this.rateLimitOverrideService.addOverride(res);
            request.subscribe({
                next: () => {
                    this.snackbarService.openSnackBar(`Rate limit exception for ${res.caller} saved.`, false);
                    this.loadRateLimitOverrides();
                },
                error: err => {
                    this.snackbarService.openSnackBar(this.overrideErrorMessage(err, 'saved'), true);
                    // A 409 can mean another admin changed the row; reload so the next edit starts from current data.
                    if (err?.status === 409) {
                        this.loadRateLimitOverrides();
                    }
                }
            });
        });
    }

    removeRateLimitOverride(override: RateLimitOverride) {
        this.dialog.open(ConfirmationDialogComponent, {
            width: '450px',
            data: {
                title: 'Delete Rate Limit Exception',
                message: `Delete the ${this.routeGroupLabels[override.route_group]} exception for "${override.caller}"? The caller returns to the default limits within 5 minutes.`,
                confirmBtnText: 'Delete',
                confirmBtnColor: 'error'
            }
        }).afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.rateLimitOverrideService.removeOverride(override.id).subscribe({
                    next: () => this.loadRateLimitOverrides(),
                    error: err => this.snackbarService.openSnackBar(this.overrideErrorMessage(err, 'deleted'), true)
                });
            }
        });
    }

    private overrideErrorMessage(err: any, action: string): string {
        // ErrorMessage.message is "400 BAD_REQUEST \"<detail>\""; show the detail only.
        const detail = (err?.error?.message || err?.error?.detail || '').replace(/^\d{3} [A-Z_]+ "(.*)"$/, '$1');
        // 409 is either a duplicate caller + area or a stale version; the API detail says which.
        if (err?.status === 409 && !detail) {
            return `Rate limit exception not ${action}: it conflicts with the current data. Reload and try again.`;
        }
        return `Rate limit exception not ${action}${detail ? ': ' + detail : '.'}`;
    }

    updateSetting() {
        let newValue ='';

        if (this.isValid) {
            Object.keys(this.settings).forEach(key => {
                switch (key) {
                    case this.DEFAULT_GROUP_OWNERS:
                    case this.DEFAULT_GROUP_MEMBERS:
                    case this.DEFAULT_SUPER_GROUP_MEMBERS:
                        newValue = this.settings[key].join();
                        if(newValue !== this.prevSettings[key].join()) {
                            this.incrementRequestCount();
                            this.dataService.updateSetting({
                                name: key,
                                value: newValue
                            }).subscribe(() => this.decrementRequestCount());
                        }
                        break;
                    case this.NEWS:
                    case this.NEWS_COLOR:
                    case this.DO_LDAP_SYNC:
                    case this.LDAP_SYNC_JOB_FREQUENCY:
                    case this.DO_ESCAL:
                    case this.ESCAL_JOB_FREQUENCY:
                    case this.ESCALATION_INTERVAL:
                    case this.REMINDER_INTERVAL:
                    case this.DO_MESSAGE_PURGE:
                    case this.MESSAGE_PURGE_JOB_FREQUENCY:
                    case this.MESSAGE_PURGE_JOB_ERROR:
                    case this.LAST_MESSAGE_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_MESSAGE_PURGE_SUCCESS_TIMESTAMP:
                    case this.MESSAGE_RETENTION:
                    case this.DO_INDIVIDUAL_PURGE:
                    case this.INDIVIDUAL_PURGE_JOB_FREQUENCY:
                    case this.INDIVIDUAL_PURGE_JOB_ERROR:
                    case this.LAST_INDIVIDUAL_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_INDIVIDUAL_PURGE_SUCCESS_TIMESTAMP:
                    case this.DO_CONTACT_PURGE:
                    case this.CONTACT_PURGE_JOB_FREQUENCY:
                    case this.CONTACT_PURGE_JOB_ERROR:
                    case this.LAST_CONTACT_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_CONTACT_PURGE_SUCCESS_TIMESTAMP:
                    case this.DO_DEVICE_PURGE:
                    case this.DEVICE_PURGE_JOB_FREQUENCY:
                    case this.DEVICE_PURGE_JOB_ERROR:
                    case this.LAST_DEVICE_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_DEVICE_PURGE_SUCCESS_TIMESTAMP:
                    case this.DO_GROUP_PURGE:
                    case this.GROUP_PURGE_JOB_FREQUENCY:
                    case this.GROUP_PURGE_JOB_ERROR:
                    case this.LAST_GROUP_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_GROUP_PURGE_SUCCESS_TIMESTAMP:
                    case this.DO_ORPHAN_PURGE:
                    case this.ORPHAN_PURGE_JOB_FREQUENCY:
                    case this.ORPHAN_PURGE_JOB_ERROR:
                    case this.LAST_ORPHAN_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_ORPHAN_PURGE_SUCCESS_TIMESTAMP:
                    case this.DO_TEMPLATE_PURGE:
                    case this.TEMPLATE_PURGE_JOB_FREQUENCY:
                    case this.TEMPLATE_PURGE_JOB_ERROR:
                    case this.LAST_TEMPLATE_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_TEMPLATE_PURGE_SUCCESS_TIMESTAMP:
                    case this.DO_TOKEN_PURGE:
                    case this.TOKEN_PURGE_JOB_FREQUENCY:
                    case this.TOKEN_PURGE_JOB_ERROR:
                    case this.LAST_TOKEN_PURGE_ATTEMPT_TIMESTAMP:
                    case this.LAST_TOKEN_PURGE_SUCCESS_TIMESTAMP:
                    case this.DO_ON_CALL:
                    case this.NOTIFICATION_OVERRIDE:
                    case this.DEFAULT_NOTIFICATION:
                    case this.ON_CALL_JOB_FREQUENCY:
                    case this.ON_CALL_JOB_ERROR:
                    case this.LAST_ON_CALL_ATTEMPT_TIMESTAMP:
                    case this.LAST_ON_CALL_SUCCESS_TIMESTAMP:
                    case this.RECIPIENT_WARNING:
                    case this.TOKEN_EXPIRATION:
                    case this.SCHEDULE_PURGE_AGE:
                    case this.MESSAGE_STATUS_V2_REFRESH_INTERVAL:
                    case this.FF_MESSAGE_TYPE:
                    case this.FF_MESSAGE_V2:
                    case this.FF_ALERTMANAGER_V2:
                    case this.FF_PAGERDUTY_V2:
                    case this.FF_ESCALATION_V2:
                    case this.FF_ACK_DEEP_LINK:
                    case this.AM_ALERTS_CAP_MODE:
                    case this.AM_ALERTS_CAP:
                        newValue = this.settings[key];
                        if(newValue !== this.prevSettings[key]) {
                            this.incrementRequestCount();
                            this.dataService.updateSetting({
                                name: key,
                                value: newValue
                            }).subscribe(() => this.decrementRequestCount());
                        }
                        break;
                }
            });
            this.prevSettings = JSON.parse(JSON.stringify(this.settings));

            this.updateOrdinal();
            for (const groupType of this.groupTypesToRemove) {
                this.incrementRequestCount();
                this.groupTypeService.removeGroupType(groupType.group_type_id).subscribe({
                    next: () => {
                        this.decrementRequestCount();
                        this.groupTypesToRemove = this.groupTypesToRemove.filter(gType => gType !== groupType);
                    }, error: () => {
                        this.snackbarService.openSnackBar('Group type ' + groupType.group_type + ' is in use and unable to be removed.', true);
                        this.requestCount = 0;
                    }
                }).add(() => {
                    // Pulls Group Types From Database Again
                    // If You Skip This Step, You Cannot Delete Group Types You Just Added Until You Refresh The Page
                    if (this.groupTypesToAdd.length === 0) {
                        this.groupTypeService.getGroupTypes().subscribe(gTypes => {
                            this.groupTypes = gTypes;
                        });
                    }
                });
            }
            for (const groupType of this.groupTypesToAdd) {
                this.incrementRequestCount();
                this.groupTypeService.addGroupType(groupType).subscribe({
                    next: () => {
                        this.decrementRequestCount();
                        this.groupTypesToAdd = this.groupTypesToAdd.filter(gType => gType !== groupType);
                    }, error: () => {
                        this.snackbarService.openSnackBar('Group type ' + groupType.group_type + ' not added. Try again, and if problem persists, notify support.', true);
                        this.requestCount = 0;
                    }
                }).add(() => {
                    // Pulls Group Types From Database Again
                    // If You Skip This Step, You Cannot Delete Group Types You Just Added Until You Refresh The Page
                    this.groupTypeService.getGroupTypes().subscribe(gTypes => {
                        this.groupTypes = gTypes;
                    });
                });
            }
            for (const contactType of this.contactTypesToRemove) {
                this.incrementRequestCount();
                this.contactTypeService.removeContactType(contactType.contact_type_id).subscribe({
                    next: () => {
                        this.decrementRequestCount();
                        this.contactTypesToRemove = this.contactTypesToRemove.filter(cType => cType !== contactType);
                    }, error: () => {
                        this.snackbarService.openSnackBar('Contact type ' + contactType.contact_type + ' is in use and unable to be removed.', true);
                        this.requestCount = 0;
                    }
                }).add(() => {
                    // Pulls Contact Types From Database Again
                    // If You Skip This Step, You Cannot Delete Contact Types You Just Added Until You Refresh The Page
                    if (this.contactTypesToAdd.length === 0) {
                        this.contactTypeService.getContactTypes().subscribe(cTypes => {
                            this.contactTypes = cTypes;
                        });
                    }
                });
            }
            for (const contactType of this.contactTypesToAdd) {
                this.incrementRequestCount();
                this.contactTypeService.addContactType(contactType).subscribe({
                    next: () => {
                        this.decrementRequestCount();
                        this.contactTypesToAdd = this.contactTypesToAdd.filter(cType => cType !== contactType);
                    }, error: () => {
                        this.snackbarService.openSnackBar('Contact type ' + contactType.contact_type + ' not added. Try again, and if problem persists, notify support.', true);
                        this.requestCount = 0;
                    }
                }).add(() => {
                    // Pulls Contact Types From Database Again
                    // If You Skip This Step, You Cannot Delete Contact Types You Just Added Until You Refresh The Page
                    this.contactTypeService.getContactTypes().subscribe(cTypes => {
                        this.contactTypes = cTypes;
                    });
                });
            }
        } else {
            this.snackbarService.openSnackBar(`Cannot save settings.`, true);
        }
    }

    addOwner(event: any) {
        if (event && !this.settings[this.DEFAULT_GROUP_OWNERS].find(e => e === event.device_name)) {
            this.settings[this.DEFAULT_GROUP_OWNERS].push(event.device_name);
        } else {
            this.snackbarService.openSnackBar(`Cannot add duplicate group owner ${event.full_name ? event.full_name : 'this'} `, true);
        }
        this.individualsAuto = null;
        this.groupOwnerInput = '';
    }

    addMember(event?: any) {
        if (event && !this.settings[this.DEFAULT_GROUP_MEMBERS].find(e => e === event.device_name)) {
            this.settings[this.DEFAULT_GROUP_MEMBERS].push(event.device_name ? event.device_name : event.proposed_device_name);
        } else {
            this.snackbarService.openSnackBar(`Cannot add duplicate group member ${event.full_name ? event.full_name : 'this'}`, true);
        }
        this.indsAndDevices = null;
        this.groupMemberInput = '';
    }

    addSuperMember(event: any) {
        if (event && !this.settings[this.DEFAULT_SUPER_GROUP_MEMBERS].find(e => e === event.device_name)) {
            this.settings[this.DEFAULT_SUPER_GROUP_MEMBERS].push(event.device_name ? event.device_name : event.proposed_device_name);
        } else {
            this.snackbarService.openSnackBar(`Cannot add duplicate superGroup member ${event.name}`, true);
        }
        this.groupsAuto = null;
        this.superGroupMemberInput = '';
    }

    lastOrdinal() {
        let topOrdinal = 0;
        for (const groupType of this.groupTypes) {
            if (groupType.ordinal > topOrdinal)
                topOrdinal = groupType.ordinal;
        }
        return topOrdinal;
    }

    drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.groupTypes, event.previousIndex, event.currentIndex);
    }

    updateOrdinal() {
        let index = 1;
        for (const groupType of this.groupTypes) {
            if (groupType.ordinal != index) {
                groupType.ordinal = index;
                this.groupTypeService.updateGroupType(groupType.group_type_id, groupType).subscribe();
            }
            index++;
        }
    }

    lastContactId() {
        let topId = 0;
        for (const contactType of this.contactTypes) {
            if (contactType.contact_type_id > topId)
                topId = contactType.contact_type_id;
        }
        return topId;
    }
}
